import { useCallback } from 'react'

import type { PERMISSION_KEYS } from './permissionKeys.generated'
import type { SessionGetCreateResponse } from '@api/orval/apiHooks'
import { useAppSelector } from '@store/hooks'
import type { RootState } from '@store/store'

/*
  Single entry point for UI access checks. Rules:
  - Gate UI on permission keys from the session (`session.keys`), never on `role`.
  - Per-record field locks come from the backend's `editable_fields`, not from keys.
  - Keys are `<resource>.<capability>` (CRUD) or `<resource>.<group>.<action>` (custom actions).
    The backend maps index/show → read, new/create → create, edit/update → update, destroy → delete.

  Example:
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('templates')
  const canApprove = usePermission('payouts.lifecycle.approve')
*/

// Every key the backend defines, across all brands. Regenerate with `pnpm permissions:sync` when the backend adds one;
// a mistyped or removed key then fails to compile instead of silently hiding UI.
export type PermissionKey = (typeof PERMISSION_KEYS)[number]

export type PermissionResource = PermissionKey extends `${infer Resource}.${string}` ? Resource : never

type CrudCapability = 'read' | 'create' | 'update' | 'delete' | 'manage'

/**
 * Builds `<resource>.<capability>` for code that picks the resource at runtime (tables, nav, tabs). Not every resource
 * has every capability (e.g. `payouts` has no `.delete`); a combination the backend doesn't define is never granted.
 */
export const crudKey = (resource: PermissionResource, capability: CrudCapability) =>
  `${resource}.${capability}` as PermissionKey

type SessionUser = Pick<SessionGetCreateResponse, 'super_user' | 'keys'> | null | undefined

/**
 * Mirrors backend `User#permission?`: super users pass, `<resource>.manage` grants the whole resource,
 * and a `<resource>.<group>` key grants every `<resource>.<group>.<action>` under it.
 */
export const hasPermission = (user: SessionUser, key: PermissionKey): boolean => {
  if (!user) return false
  if (user.super_user) return true

  const [resource, group, action] = key.split('.')
  const keys = user.keys ?? []

  // Only a 3-part action key has a parent group; for a CRUD key `<resource>.<group>` is the key itself.
  return (
    keys.includes(key) || keys.includes(`${resource}.manage`) || (!!action && keys.includes(`${resource}.${group}`))
  )
}

/** Whether the session may use the CRM at all: a user with no keys has nothing to see. Replaces the old role allowlist. */
export const hasAnyAccess = (user: SessionUser): boolean => !!user && (user.super_user || (user.keys?.length ?? 0) > 0)

const selectHasPermission = (key: PermissionKey) => (state: RootState) => hasPermission(state.session.user, key)

export const usePermission = (key: PermissionKey) => useAppSelector(selectHasPermission(key))

export const useResourcePermissions = (resource: PermissionResource) => ({
  canRead: usePermission(crudKey(resource, 'read')),
  canCreate: usePermission(crudKey(resource, 'create')),
  canUpdate: usePermission(crudKey(resource, 'update')),
  canDelete: usePermission(crudKey(resource, 'delete')),
})

type GatableQuery = { data: unknown; error: unknown; isPending: boolean; refetch: () => unknown }

const noopRefetch = () => undefined

/**
 * For a query behind a permission key. Pass the same flag as the query's `enabled`, then read the query through
 * this: a disabled query reports `isPending` forever, and `refetch()` ignores `enabled` (it would hit a 403).
 *
 * Example:
 * const canRead = usePermission('users.other_actions.statistics')
 * const statistics = gateQuery(canRead, useGetUserStatistics(userId, { query: { enabled: canRead } }))
 */
// Generic over the whole result: TanStack's result is a union of states, and inferring `data` alone collapses to `never`.
export const gateQuery = <TQuery extends GatableQuery>(allowed: boolean, query: TQuery) => ({
  allowed,
  data: allowed ? (query.data as TQuery['data']) : undefined,
  error: allowed ? (query.error as TQuery['error']) : null,
  isPending: allowed && query.isPending,
  refetch: allowed ? query.refetch : noopRefetch,
})

/**
 * For lists gated item-by-item (tabs, menu entries), where one hook per item would break the rules of hooks.
 *
 * Example:
 * const can = usePermissionCheck()
 * const tabs = allTabs.filter((tab) => can(crudKey(tab.resource, 'read')))
 */
export const usePermissionCheck = () => {
  const user = useAppSelector((state) => state.session.user)

  return useCallback((key: PermissionKey) => hasPermission(user, key), [user])
}
